import {
  type GetPresentationsByDemoArgs,
  useLazyGetPresentationsByDemoQuery,
} from '@carboncopyco/data';
import { PlayIcon } from '@carboncopyco/icons';
import type { EnhancedPresentation } from '@carboncopyco/types';
import {
  Badge,
  Button,
  Container,
  SkeletonRowLoading,
  TextBase,
  TooltipBubble,
  UserBasicInfo,
} from '@carboncopyco/ui';
import { isDesktop } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useNavigate } from 'react-router-dom';
import { type Col, FixedTable } from '../../../FixedTable';
import { GiveADemoModal } from '../../../PresentationModals';
import { Screenshot } from '../../../Screenshot';
import { useControlledFetch } from '../../Controls/useControlledFetch';
import { CreatedOnTooltip } from '../../CreatedOnTooltip';
import { EmptyState } from '../../EmptyState';
import { TableRowWrapper } from '../../TableRowWrapper';
import { handleSinglePlural } from '../../utils';
import { useOverlayPageProps } from '../OverlayPageContext';
import { PresentationRowButtons } from './PresentationRowButtons';
import { PresentationsControls } from './PresentationsControls';

const cols: Col<EnhancedPresentation>[] = [
  {
    alignment: 'left',
    title: 'Opportunity',
    width: 220,
    maxContentWidth: 200,
    render: (presentation) => {
      return (
        <div className="flex items-center justify-between gap-3 w-full">
          <div className="w-14 h-9 shrink-0">
            <Screenshot
              image={presentation.image}
              alt="demo"
              rounded="sm"
              isOverlayDemo
            />
          </div>
          <div className="flex flex-col flex-grow overflow-hidden">
            <TooltipBubble
              disabled={presentation.forClient.length < 18}
              placement="top-start"
              title={presentation.forClient}
            >
              <div className="overflow-hidden truncate text-ds-natural-600">
                <TextBase
                  ellipsis
                  color="text-ds-natural-600"
                  label={presentation.forClient}
                />
              </div>
              <div className="flex gap-1">
                <Badge
                  label={`V${String(presentation.revisionId + 1)}`}
                  variant="grey"
                />
              </div>
            </TooltipBubble>
          </div>
        </div>
      );
    },
  },
  {
    title: 'Date',
    alignment: 'left',
    width: 120,
    maxContentWidth: 116,
    render: ({ createTime }) => <CreatedOnTooltip dateTime={createTime} />,
  },
  {
    alignment: 'left',
    title: 'Creator',
    width: 150,
    maxContentWidth: 130,
    render: ({ creator }) => (
      <div className="flex items-center">
        <UserBasicInfo
          size="small"
          user={creator}
          textColor="text-ds-natural-600"
          truncateWidth={110}
          tooltip={creator.name.length > 15}
        />
      </div>
    ),
  },
  {
    alignment: 'right',
    title: 'Presented',
    width: 85,
    render: ({ totalPresentationViews }) => (
      <TooltipBubble
        title={handleSinglePlural(totalPresentationViews, 'Presented')}
        placement="top"
      >
        <TextBase
          label={totalPresentationViews || '-'}
          color="text-ds-natural-600"
        />
      </TooltipBubble>
    ),
  },
  {
    alignment: 'right',
    title: '',
    width: 400,
    render: (presentation) => (
      <PresentationRowButtons presentation={presentation} />
    ),
  },
];

export const PresentationsTable = () => {
  const {
    rootPath,
    handleCreatePresentation,
    organizationId,
    demoId,
    goToDemoPreview,
    playPresentation,
    desktopDomain,
    workspaceId,
  } = useOverlayPageProps();

  const [
    showGiveADemoModal,
    {
      setTrue: setTrueShowGiveADemoModal,
      setFalse: setFalseShowGiveADemoModal,
    },
  ] = useBoolean();
  const navigate = useNavigate();

  const {
    items: presentations,
    fetchNextControlledBatch,
    isLoading,
    isLoadingBatch,
    error,
  } = useControlledFetch<EnhancedPresentation, GetPresentationsByDemoArgs>({
    lazyHook: useLazyGetPresentationsByDemoQuery,
    extraEndpointArgs: {
      organizationId,
      workspaceId,
      demoId,
    },
    mapUrlStateToEndpointArgs: {
      sort: 'sort',
      creators: 'creators',
      companies: 'companies',
      query: 'query',
    },
  });

  if (!isLoading && !presentations?.length) {
    return (
      <>
        <EmptyState
          title="Demo has no active presentations"
          description="Once a demo presentation is created, you can track it from here"
          action={
            <Button
              link
              blue
              label="Give a demo"
              leftIcon={<PlayIcon />}
              onClick={() =>
                isDesktop()
                  ? handleCreatePresentation?.()
                  : setTrueShowGiveADemoModal()
              }
            />
          }
        />
        {showGiveADemoModal && (
          <GiveADemoModal
            workspaceId={workspaceId}
            onClose={setFalseShowGiveADemoModal}
            open={showGiveADemoModal}
            demoId={demoId}
            organizationId={organizationId}
            goToPresentationPage={playPresentation}
            goToDemoPresentations={() => {
              navigate(`${rootPath}/presentations`);
            }}
            goToDemoPreview={goToDemoPreview}
            desktopDomain={desktopDomain}
          />
        )}
      </>
    );
  }

  return (
    <>
      <PresentationsControls />
      <div className="py-5">
        <FixedTable<EnhancedPresentation>
          error={error}
          data={presentations ?? []}
          cols={cols}
          isLoading={isLoading}
          handleFetchNextBatch={fetchNextControlledBatch}
          isLoadingBatch={isLoadingBatch}
          sideSpacing
          infiniteListMaxHeight={600}
          rowWrapper={TableRowWrapper}
          loader={
            <Container>
              <div className="w-ds-lg-modal">
                <SkeletonRowLoading
                  medium
                  itemsCount={5}
                />
              </div>
            </Container>
          }
        />
      </div>
    </>
  );
};
